import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveIcon from "@mui/icons-material/Save";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { captureException } from "@sentry/react";
import IdBadge from "components/IdBadge";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { regionKey } from "utils/regionKey";
import { useLidarClearance } from "./LidarClearanceContext";
import StopValueFields from "./StopValueFields";
import { replaceColorScaleStops } from "./api";
import type { LidarClearanceColorScale } from "./schemas";
import t from "./translations";
import { newStopRow, useStopRows } from "./useStopRows";

type Props = {
  scale: LidarClearanceColorScale | null;
  onClose: () => void;
};

function IdNameCell({
  region,
  id,
  name,
}: {
  region: string;
  id: number;
  name: string | number;
}) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <IdBadge prefix={region.toUpperCase()} id={id} />
      <Typography
        variant="body2"
        sx={{ flexGrow: 1, minWidth: 0, wordBreak: "break-word" }}
      >
        {name}
      </Typography>
    </Stack>
  );
}

export default function ColorScaleDetailsDrawer({ scale, onClose }: Props) {
  const {
    axesByColorScaleKey,
    systemAxisLinksByColorScaleKey,
    systemsByRegionId,
    refetch,
  } = useLidarClearance();
  const { stops, setStops, addStop, updateStop, removeStop } = useStopRows();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStops(scale ? scale.colors.map(newStopRow) : []);
  }, [scale, setStops]);

  if (!scale) return null;

  const scaleKey = regionKey(scale.region, scale.color_scale_id);
  const usingAxes = axesByColorScaleKey.get(scaleKey) ?? [];
  const usingSystemLinks = systemAxisLinksByColorScaleKey.get(scaleKey) ?? [];

  const original = scale.colors;
  const dirty =
    stops.length !== original.length ||
    stops.some((stop, index) => stop.color !== original[index]);
  const canSave = dirty && !saving;

  const save = async () => {
    setSaving(true);
    try {
      await replaceColorScaleStops(
        scale.region,
        scale.color_scale_id,
        stops.map((stop) => stop.color)
      );
      await refetch();
    } catch (error) {
      captureException(error, {
        tags: { feature: "lidarClearance.updateColorScale" },
      });
      toast.error(t.FailedToUpdateColorScale);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer anchor="right" open={scale !== null} onClose={onClose}>
      <Box sx={{ width: 420, p: 3 }} data-testid="color-scale-details-drawer">
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <IdBadge
              prefix={scale.region.toUpperCase()}
              id={scale.color_scale_id}
            />
          </Box>
          <Tooltip title={t.SaveButton}>
            <span>
              <IconButton
                color="primary"
                disabled={!canSave}
                onClick={save}
                data-testid="color-scale-drawer.save"
              >
                <SaveIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        <Stack>
          {stops.map((stop, index) => (
            <Stack
              key={stop.key}
              direction="row"
              spacing={1}
              sx={{
                alignItems: "center",
                px: 1,
                py: 1,
                backgroundColor:
                  index % 2 === 0 ? "background.paper" : "grey.100",
              }}
            >
              <StopValueFields
                position={index + 1}
                color={stop.color}
                onColorChange={(color) => updateStop(stop.key, color)}
                disabled={saving}
              />
              <IconButton
                size="small"
                aria-label={t.RemoveStop}
                onClick={() => removeStop(stop.key)}
                disabled={saving}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>

        <Button
          startIcon={<AddIcon />}
          onClick={addStop}
          disabled={saving}
          sx={{ alignSelf: "flex-start", mt: 2 }}
        >
          {t.AddStopButton}
        </Button>

        <Divider sx={{ my: 3 }} />

        <Accordion defaultExpanded disableGutters>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            data-testid="color-scale-drawer.used-by-axes-summary"
          >
            <Typography variant="subtitle1">
              {t.UsedAsDefaultColorTitle(usingAxes.length)}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {usingAxes.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                {t.NoAxesUseColorScale}
              </Typography>
            ) : (
              <TableContainer
                component={Paper}
                data-testid="color-scale-drawer.used-by-axes"
              >
                <Table size="small" sx={{ "& .MuiTableCell-root": { px: 1 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t.AxisColumn}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usingAxes.map((axis) => (
                      <TableRow key={axis.id}>
                        <TableCell>
                          <IdNameCell
                            region={axis.region}
                            id={axis.id}
                            name={axis.name_en}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded disableGutters>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            data-testid="color-scale-drawer.used-in-systems-summary"
          >
            <Typography variant="subtitle1">
              {t.UsedInSystemsTitle(usingSystemLinks.length)}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {usingSystemLinks.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                {t.NoSystemsUseColorScale}
              </Typography>
            ) : (
              <TableContainer
                component={Paper}
                data-testid="color-scale-drawer.used-in-systems"
              >
                <Table size="small" sx={{ "& .MuiTableCell-root": { px: 1 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t.SystemColumn}</TableCell>
                      <TableCell>{t.AxisColumn}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usingSystemLinks.map(({ link, axis }) => {
                      const system = systemsByRegionId.get(
                        regionKey(link.region, link.system_id)
                      );
                      return (
                        <TableRow key={link.id}>
                          <TableCell>
                            <IdNameCell
                              region={link.region}
                              id={link.system_id}
                              name={system?.name ?? link.system_id}
                            />
                          </TableCell>
                          <TableCell>
                            <IdNameCell
                              region={axis.region}
                              id={axis.id}
                              name={axis.name_en}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
    </Drawer>
  );
}
